import React, { useCallback, useEffect, useMemo, useState } from "react";
import Select from "react-select";
import { DaysTranslations } from "../../constants/index.js";
import check from "../../assets/check.svg";
import Button from "../comman/button.js";
import Modal from "../comman/modal.js";
import LoadingSpinner from "../LoadingSpinner/loadingSpinner.js";
import companyService from "../../services/company-service.js";
import { getTranslation } from "../../utils/translate.js";
import { useLanguage } from "../../utils/languageContext.js";
import { useDarkMode } from "../../utils/darkModeContext.js";

/**
 * Per-company delivery schedule override.
 *
 * Backend contract (GET/PUT /kms/partner-company/:id/schedule):
 * - dayOfWeek is 1 (Monday) .. 7 (Sunday). DaysTranslations here is 0-based, hence the +1 / -1.
 * - Empty `days` = the company follows the chef's own working days.
 * - Non-empty `days` = a weekday not listed is closed for that company.
 * - RANGE days carry timeFrom/timeTo only; SLOTS days carry an ascending `slots` list only.
 */

const DAY_INDEXES = [0, 1, 2, 3, 4, 5, 6];
const MAX_LEAD_TIME_HOURS = 168;

const TIME_OPTIONS = (() => {
    const options = [];
    for (let hour = 0; hour < 24; hour++) {
        const h = hour.toString().padStart(2, "0");
        options.push({ value: `${h}:00`, label: `${h}:00` });
        options.push({ value: `${h}:30`, label: `${h}:30` });
    }
    return options;
})();

/** Backend may return "HH:mm:ss"; the UI works in "HH:mm". */
const toHHmm = (time) => (typeof time === "string" ? time.slice(0, 5) : time);

const toDraft = (schedule) => {
    const days = {};
    (schedule?.days || []).forEach((day) => {
        days[day.dayOfWeek - 1] = {
            mode: day.mode,
            timeFrom: toHHmm(day.timeFrom) || "09:00",
            timeTo: toHHmm(day.timeTo) || "18:00",
            slots: (day.slots || []).map(toHHmm)
        };
    });
    return { days, leadTimeHours: String(schedule?.leadTimeHours ?? 0) };
};

const toPayload = (draft) => ({
    days: Object.entries(draft.days)
        .sort(([a], [b]) => Number(a) - Number(b))
        .map(([index, day]) =>
            day.mode === "SLOTS"
                ? { dayOfWeek: Number(index) + 1, mode: "SLOTS", slots: [...day.slots].sort() }
                : { dayOfWeek: Number(index) + 1, mode: "RANGE", timeFrom: day.timeFrom, timeTo: day.timeTo }
        ),
    leadTimeHours: Number(draft.leadTimeHours)
});

const useSelectStyles = (isDarkMode, hasError = false) =>
    useMemo(
        () => ({
            control: (provided, state) => ({
                ...provided,
                minHeight: "36px",
                height: "36px",
                paddingRight: "8px",
                backgroundColor: isDarkMode ? "#0d1117" : "#ffffff",
                borderColor: hasError ? "#f56565" : isDarkMode ? "#30363d" : state.isFocused ? "#cfcfcf" : "#e5e7eb",
                boxShadow: hasError ? "0 0 0 1px #f56565" : "none",
                "&:hover": { borderColor: hasError ? "#f56565" : isDarkMode ? "#30363d" : "#cfcfcf" }
            }),
            singleValue: (provided) => ({
                ...provided,
                color: isDarkMode ? "#c9d1d9" : "#374151",
                fontSize: "14px",
                fontWeight: "500"
            }),
            placeholder: (provided) => ({ ...provided, fontSize: "14px" }),
            menu: (provided) => ({
                ...provided,
                zIndex: 10000,
                backgroundColor: isDarkMode ? "#0d1117" : "#ffffff"
            }),
            menuPortal: (provided) => ({ ...provided, zIndex: 10000 }),
            option: (provided, state) => ({
                ...provided,
                backgroundColor:
                    state.isSelected || state.isFocused
                        ? isDarkMode ? "#1c2128" : "#f3f4f6"
                        : isDarkMode ? "#0d1117" : "#ffffff",
                color: isDarkMode ? "#c9d1d9" : "#374151",
                fontSize: "14px"
            }),
            dropdownIndicator: (provided) => ({
                ...provided,
                color: isDarkMode ? "#8b949e" : "#6b7280",
                padding: "4px"
            })
        }),
        [isDarkMode, hasError]
    );

const TimeSelect = ({ value, onChange, options = TIME_OPTIONS, placeholder, hasError }) => {
    const { isDarkMode } = useDarkMode();
    const styles = useSelectStyles(isDarkMode, hasError);
    return (
        <div className="w-[100px]">
            <Select
                value={options.find((opt) => opt.value === value) || null}
                onChange={(option) => onChange(option.value)}
                options={options}
                placeholder={placeholder}
                menuPortalTarget={document.body}
                menuPosition="fixed"
                styles={styles}
            />
        </div>
    );
};

const summarizeDay = (day, language) => {
    const name = DaysTranslations[day.dayOfWeek - 1]?.[language];
    if (day.mode === "SLOTS") {
        const slots = (day.slots || []).map(toHHmm);
        return slots.length <= 3
            ? `${name} (${slots.join(", ")})`
            : `${name} (${slots.length} ${getTranslation("schedule.slotsCount", language)})`;
    }
    return `${name} (${toHHmm(day.timeFrom)} - ${toHHmm(day.timeTo)})`;
};

const CompanySchedules = ({ toastRef }) => {
    const { language } = useLanguage();
    const [companies, setCompanies] = useState([]);
    const [schedules, setSchedules] = useState({});
    const [loading, setLoading] = useState(true);
    const [editingCompany, setEditingCompany] = useState(null);
    const [draft, setDraft] = useState(null);
    const [errors, setErrors] = useState({});
    const [isSaving, setIsSaving] = useState(false);

    const t = (key) => getTranslation(key, language);

    const loadSchedules = useCallback(async () => {
        setLoading(true);
        try {
            const list = (await companyService.getCompanies()) || [];
            const entries = await Promise.all(
                list.map(async (company) => [company.id, await companyService.getCompanySchedule(company.id)])
            );
            setCompanies(list);
            setSchedules(Object.fromEntries(entries));
        } catch (error) {
            console.error("Failed to load company schedules:", error);
            toastRef?.current?.addToast(getTranslation("schedule.companyScheduleLoadError", language), "error");
        } finally {
            setLoading(false);
        }
    }, [toastRef, language]);

    useEffect(() => {
        loadSchedules();
        // Load once on mount; language changes must not refetch.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const openEditor = (company) => {
        setEditingCompany(company);
        setDraft(toDraft(schedules[company.id]));
        setErrors({});
    };

    const closeEditor = () => {
        setEditingCompany(null);
        setDraft(null);
        setErrors({});
    };

    const updateDay = (index, patch) => {
        setDraft((prev) => ({ ...prev, days: { ...prev.days, [index]: { ...prev.days[index], ...patch } } }));
        setErrors((prev) => {
            const next = { ...prev };
            delete next[index];
            return next;
        });
    };

    const toggleDay = (index) => {
        setDraft((prev) => {
            const days = { ...prev.days };
            if (days[index]) {
                delete days[index];
            } else {
                days[index] = { mode: "RANGE", timeFrom: "09:00", timeTo: "18:00", slots: [] };
            }
            return { ...prev, days };
        });
    };

    const validate = () => {
        const next = {};
        Object.entries(draft.days).forEach(([index, day]) => {
            if (day.mode === "SLOTS" && day.slots.length === 0) {
                next[index] = t("schedule.noSlotsError");
            }
            if (day.mode === "RANGE" && day.timeFrom >= day.timeTo) {
                next[index] = t("schedule.invalidTimeRange");
            }
        });
        const lead = Number(draft.leadTimeHours);
        if (draft.leadTimeHours === "" || !Number.isInteger(lead) || lead < 0 || lead > MAX_LEAD_TIME_HOURS) {
            next.leadTimeHours = t("schedule.invalidLeadTime");
        }
        setErrors(next);
        return Object.keys(next).length === 0;
    };

    const save = async (payload) => {
        setIsSaving(true);
        try {
            const saved = await companyService.updateCompanySchedule(editingCompany.id, payload);
            setSchedules((prev) => ({ ...prev, [editingCompany.id]: saved }));
            toastRef?.current?.addToast(t("schedule.scheduleUpdated"), "success");
            closeEditor();
        } catch (error) {
            const message = error.response?.data?.developerMessage || error.response?.data?.message;
            toastRef?.current?.addToast(message || t("schedule.updateError"), "error");
        } finally {
            setIsSaving(false);
        }
    };

    const handleSave = () => {
        if (validate()) {
            save(toPayload(draft));
        }
    };

    // Reset drops the day overrides only, so an unfinished day editor must not block it.
    const handleReset = () => {
        const lead = Number(draft.leadTimeHours);
        const leadTimeValid = draft.leadTimeHours !== "" && Number.isInteger(lead) && lead >= 0 && lead <= MAX_LEAD_TIME_HOURS;
        save({ days: [], leadTimeHours: leadTimeValid ? lead : 0 });
    };

    return (
        <div className="bg-white dark:bg-[#161b22] rounded-lg shadow-sm border dark:border-[#30363d] p-5 mb-8">
            <h2 className="text-lg font-semibold mb-2 text-gray-900 dark:text-[#c9d1d9]">{t("schedule.companySchedules")}</h2>
            <p className="text-sm text-gray-600 dark:text-[#8b949e] mb-4 font-medium">{t("schedule.companySchedulesDescription")}</p>

            {loading ? (
                <LoadingSpinner />
            ) : companies.length === 0 ? (
                <div className="my-4 p-4 bg-white dark:bg-[#0d1117] rounded-md text-center border dark:border-[#30363d]">
                    <p className="text-sm text-gray-500 dark:text-[#8b949e] italic font-medium">{t("schedule.noCompanies")}</p>
                </div>
            ) : (
                <div className="divide-y dark:divide-[#30363d] border border-gray-200 dark:border-[#30363d] rounded-md">
                    {companies.map((company) => {
                        const schedule = schedules[company.id];
                        const days = [...(schedule?.days || [])].sort((a, b) => a.dayOfWeek - b.dayOfWeek);
                        const leadTime = schedule?.leadTimeHours ?? 0;
                        return (
                            <div key={company.id} className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 px-4 py-3">
                                <div className="min-w-0">
                                    <p className="text-sm font-semibold text-gray-900 dark:text-[#c9d1d9]">{company.name}</p>
                                    <div className="flex flex-wrap gap-2 mt-2">
                                        {days.length === 0 ? (
                                            <span className="text-sm text-gray-500 dark:text-[#8b949e] italic">{t("schedule.usesDefaultSchedule")}</span>
                                        ) : (
                                            days.map((day) => (
                                                <span
                                                    key={day.dayOfWeek}
                                                    className="px-3 py-1 rounded-md text-xs border border-gray-200 dark:border-[#30363d] text-gray-900 dark:text-[#c9d1d9] font-medium bg-white dark:bg-[#0d1117]"
                                                >
                                                    {summarizeDay(day, language)}
                                                </span>
                                            ))
                                        )}
                                        {leadTime > 0 && (
                                            <span className="px-3 py-1 rounded-md text-xs border border-blue-200 dark:border-[#1f6feb] text-blue-700 dark:text-[#58a6ff] font-medium">
                                                {t("schedule.leadTimeShort")}: {leadTime}{t("schedule.hoursShort")}
                                            </span>
                                        )}
                                    </div>
                                </div>
                                <Button variant="secondary" size="sm" onClick={() => openEditor(company)} className="font-bold shrink-0">
                                    {t("common.edit")}
                                </Button>
                            </div>
                        );
                    })}
                </div>
            )}

            <Modal isOpen={Boolean(editingCompany && draft)} onClose={closeEditor} title={editingCompany?.name} size="md">
                {draft && (
                    <div className="space-y-4">
                        <p className="text-sm text-gray-600 dark:text-[#8b949e] font-medium">{t("schedule.companyScheduleHint")}</p>

                        <div>
                            <label className="block text-sm text-gray-700 dark:text-[#b1bac4] font-medium mb-1">
                                {t("schedule.leadTimeHours")}
                            </label>
                            <input
                                type="number"
                                min={0}
                                max={MAX_LEAD_TIME_HOURS}
                                step={1}
                                value={draft.leadTimeHours}
                                onChange={(e) => {
                                    const value = e.target.value;
                                    setDraft((prev) => ({ ...prev, leadTimeHours: value }));
                                    setErrors((prev) => {
                                        const next = { ...prev };
                                        delete next.leadTimeHours;
                                        return next;
                                    });
                                }}
                                className={`w-[120px] h-9 px-3 rounded-md border text-sm bg-white dark:bg-[#0d1117] text-gray-900 dark:text-[#c9d1d9] ${errors.leadTimeHours ? "border-red-500 dark:border-red-400" : "border-gray-200 dark:border-[#30363d]"}`}
                            />
                            {errors.leadTimeHours && (
                                <p className="text-red-500 dark:text-red-400 text-xs mt-1 font-medium">{errors.leadTimeHours}</p>
                            )}
                        </div>

                        <div className="max-h-[400px] overflow-y-auto pr-2">
                            {DAY_INDEXES.map((index) => {
                                const day = draft.days[index];
                                return (
                                    <div key={index} className="mb-3">
                                        <div
                                            className="cursor-pointer px-4 py-3 flex justify-between items-center hover:bg-gray-50 dark:hover:bg-[#1c2128] rounded-md transition-colors text-gray-900 dark:text-[#c9d1d9] font-medium border border-gray-200 dark:border-[#30363d]"
                                            onClick={() => toggleDay(index)}
                                        >
                                            <span>{DaysTranslations[index][language]}</span>
                                            <div className="w-5 h-5 border dark:border-[#30363d] rounded flex items-center justify-center bg-white dark:bg-[#0d1117]">
                                                {day && <img src={check} alt="check" className="w-4 h-4 dark:invert dark:opacity-80" />}
                                            </div>
                                        </div>
                                        {day && (
                                            <DayEditor
                                                day={day}
                                                error={errors[index]}
                                                onChange={(patch) => updateDay(index, patch)}
                                                t={t}
                                            />
                                        )}
                                    </div>
                                );
                            })}
                        </div>

                        <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t dark:border-[#30363d]">
                            <Button
                                variant="ghost"
                                size="md"
                                type="button"
                                onClick={handleReset}
                                disabled={isSaving}
                                className="sm:mr-auto font-semibold"
                            >
                                {t("schedule.resetToDefault")}
                            </Button>
                            <Button variant="secondary" size="md" type="button" onClick={closeEditor} className="font-semibold">
                                {t("common.cancel")}
                            </Button>
                            <Button
                                variant="primary"
                                size="md"
                                type="button"
                                onClick={handleSave}
                                disabled={isSaving}
                                loading={isSaving}
                                className="font-semibold"
                            >
                                {t("common.save")}
                            </Button>
                        </div>
                    </div>
                )}
            </Modal>
        </div>
    );
};

const DayEditor = ({ day, error, onChange, t }) => {
    const modeButton = (mode, label) => (
        <button
            type="button"
            onClick={() => onChange({ mode })}
            className={`px-3 py-1 text-xs font-semibold rounded-md border transition-colors ${
                day.mode === mode
                    ? "bg-gray-900 text-white border-gray-900 dark:bg-[#c9d1d9] dark:text-[#0d1117] dark:border-[#c9d1d9]"
                    : "bg-white text-gray-700 border-gray-200 dark:bg-[#0d1117] dark:text-[#b1bac4] dark:border-[#30363d]"
            }`}
        >
            {label}
        </button>
    );

    const availableSlotOptions = TIME_OPTIONS.filter((opt) => !day.slots.includes(opt.value));

    return (
        <div className={`px-4 py-3 mt-2 bg-white dark:bg-[#0d1117] rounded-md border ${error ? "border-red-500 dark:border-red-400" : "border-gray-200 dark:border-[#30363d]"}`}>
            <div className="flex gap-2 mb-3">
                {modeButton("RANGE", t("schedule.modeRange"))}
                {modeButton("SLOTS", t("schedule.modeSlots"))}
            </div>

            {day.mode === "RANGE" ? (
                <div className="flex items-center gap-3">
                    <label className="text-sm text-gray-700 dark:text-[#b1bac4] font-medium whitespace-nowrap">{t("schedule.from")}:</label>
                    <TimeSelect value={day.timeFrom} onChange={(value) => onChange({ timeFrom: value })} hasError={Boolean(error)} />
                    <span className="text-gray-700 dark:text-[#b1bac4] font-medium">-</span>
                    <label className="text-sm text-gray-700 dark:text-[#b1bac4] font-medium whitespace-nowrap">{t("schedule.to")}:</label>
                    <TimeSelect value={day.timeTo} onChange={(value) => onChange({ timeTo: value })} hasError={Boolean(error)} />
                </div>
            ) : (
                <div className="flex flex-wrap items-center gap-2">
                    {[...day.slots].sort().map((slot) => (
                        <span
                            key={slot}
                            className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium border border-gray-200 dark:border-[#30363d] text-gray-900 dark:text-[#c9d1d9]"
                        >
                            {slot}
                            <button
                                type="button"
                                aria-label={`remove ${slot}`}
                                onClick={() => onChange({ slots: day.slots.filter((s) => s !== slot) })}
                                className="text-gray-500 hover:text-red-600 dark:text-[#8b949e] dark:hover:text-red-400"
                            >
                                ×
                            </button>
                        </span>
                    ))}
                    <TimeSelect
                        value={null}
                        options={availableSlotOptions}
                        placeholder={t("schedule.addSlot")}
                        onChange={(value) => onChange({ slots: [...day.slots, value] })}
                        hasError={Boolean(error)}
                    />
                </div>
            )}

            {error && <p className="text-red-500 dark:text-red-400 text-xs mt-2 font-medium">{error}</p>}
        </div>
    );
};

export default CompanySchedules;
